import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const governmentUnit2Notes: UnitNotes = {
  unitId: "unit-2",
  unitName: "Unit 2: Political Institutions",
  examWeight: "20–25%",
  focus: "How the structure and function of political institutions reflect the allocation of power within a political system.",
  sections: [
    {
      id: "topic-2-1",
      title: "Topic 2.1: Parliamentary, Presidential, and Semi-Presidential Systems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Parliamentary Systems</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">The executive and legislative branches are fused. The head of government is chosen from the legislature (the majority party or coalition).</p>
            </div>
            <div className="space-y-3">
              <KeyTerm 
                term="Fusion of Powers" 
                definition="No strict separation between executive (Cabinet) and legislature (Parliament)."
              />
              <KeyTerm 
                term="Selection" 
                definition="Head of Government is not directly elected by people but by the legislature."
              />
              <KeyTerm 
                term="Removal" 
                definition="Legislature can remove executive through a Vote of No Confidence."
              />
              <KeyTerm 
                term="Dual Executive" 
                definition="Distinct Head of State (Ceremonial) and Head of Government (Policy/Power)."
              />
            </div>
            <div className="bg-blue-100 p-3 rounded-lg mt-4">
              <p className="text-sm text-blue-800"><strong>🇬🇧 Course Country: United Kingdom</strong></p>
              <p className="text-sm text-slate-700 mt-1">Head of Govt: Prime Minister (Leader of majority in House of Commons)</p>
              <p className="text-sm text-slate-700">Head of State: The Monarch (King Charles III)</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗳️ Presidential Systems</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <h4 className="font-bold text-green-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">Executive and legislative branches are separate and independent. Head of government is directly elected by the people.</p>
            </div>
            <div className="space-y-3">
              <KeyTerm 
                term="Separation of Powers" 
                definition="Executive and legislature have distinct mandates and terms."
              />
              <KeyTerm 
                term="Checks and Balances" 
                definition="Strong constitutional mechanisms prevent one branch from dominating."
              />
              <KeyTerm 
                term="Selection" 
                definition="Direct popular election for the president."
              />
              <KeyTerm 
                term="Removal" 
                definition="Difficult; requires impeachment (legal process for wrongdoing), not just political disagreement."
              />
              <KeyTerm 
                term="Single Executive" 
                definition="President is usually both Head of State and Head of Government."
              />
            </div>
            <div className="bg-green-100 p-3 rounded-lg mt-4">
              <p className="text-sm text-green-800"><strong>🇲🇽 Course Countries: Mexico and 🇳🇬 Nigeria</strong></p>
              <p className="text-sm text-slate-700 mt-1">President appoints cabinet (approved by legislature) but cabinet members are not usually sitting legislators.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Semi-Presidential Systems</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mb-4">
              <h4 className="font-bold text-orange-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">A hybrid system featuring two executives: a directly elected President and a Prime Minister responsible to the legislature.</p>
            </div>
            <div className="space-y-3">
              <div className="bg-orange-100 p-3 rounded-lg">
                <h5 className="font-bold text-orange-800">Dual Executive:</h5>
                <p className="text-sm text-slate-700 mt-1"><strong>President:</strong> Directly elected, significant foreign policy/military power, Head of State.</p>
                <p className="text-sm text-slate-700"><strong>Prime Minister:</strong> Nominated by President/approved by Legislature, manages domestic policy/economy, Head of Government.</p>
              </div>
              <KeyTerm 
                term="Accountability" 
                definition="Cabinet/PM is accountable to both President and Legislature."
              />
            </div>
            <div className="bg-orange-100 p-3 rounded-lg mt-4">
              <p className="text-sm text-orange-800"><strong>🇷🇺 Course Country: Russia</strong></p>
              <p className="text-sm text-slate-700 mt-1">President (Putin) holds bulk of power; PM (Mishustin) focuses on economic administration.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-2",
      title: "Topic 2.2: Comparing Systems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚙️ Efficiency in Policy Making</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Parliamentary (UK)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>High Efficiency:</strong> Executive and legislative majority are the same party, so passing legislation is usually fast and easy.</p>
                <p className="text-xs text-blue-600 mt-2 italic">Risk: "Elective Dictatorship" (few checks on the majority)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Presidential (Mexico, Nigeria)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Lower Efficiency:</strong> Checks and balances and potential divided government can lead to gridlock.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Semi-Presidential (Russia)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Variable:</strong> In Russia, President controls dominant party (United Russia), so efficiency is extremely high.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Accountability and Removal</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Parliamentary</h4>
                <p className="text-sm text-slate-700 mt-2">Executive directly accountable to legislature via Question Time and Votes of No Confidence.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Presidential</h4>
                <p className="text-sm text-slate-700 mt-2">Executive accountable to voters (fixed terms). Removal only for "high crimes" (impeachment).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-3",
      title: "Topic 2.3: Executive Systems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇨🇳 China (Authoritarian/Party-State)</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4">
              <p className="text-sm text-slate-700"><strong>Structure:</strong> Parallel hierarchies (State, Party, Military). The Party controls the State.</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Head of State:</strong> President (Xi Jinping) - largely ceremonial</p>
                <p className="text-sm text-slate-700"><strong>Head of Government:</strong> Premier (Li Qiang) - manages bureaucracy and economy</p>
                <p className="text-sm text-slate-700"><strong>True Power:</strong> General Secretary of CCP and Chairman of Central Military Commission (Xi holds all three)</p>
                <p className="text-sm text-slate-700"><strong>Selection:</strong> Behind closed doors by Politburo Standing Committee; rubber-stamped by NPC</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇮🇷 Iran (Theocratic Republic)</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <div className="space-y-3">
                <div>
                  <h4 className="font-bold text-green-800">Supreme Leader (Highest Authority)</h4>
                  <p className="text-sm text-slate-700 mt-1">Ayatollah Khamenei</p>
                  <p className="text-xs text-green-600 mt-1">Powers: Commander-in-chief, appoints head of judiciary, appoints half of Guardian Council</p>
                  <p className="text-xs text-green-600">Selection: Appointed for life by Assembly of Experts</p>
                </div>
                <div>
                  <h4 className="font-bold text-green-800">President</h4>
                  <p className="text-sm text-slate-700 mt-1">Head of Government</p>
                  <p className="text-xs text-green-600 mt-1">Powers: Drafts budget, proposes legislation, conducts foreign policy (under SL supervision)</p>
                  <p className="text-xs text-green-600">Selection: Directly elected (2-round runoff), but candidates must be vetted by Guardian Council</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇲🇽 Mexico (Federal Republic)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-sm text-slate-700"><strong>Executive:</strong> President (Head of State + Head of Government)</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Powers:</strong> Commander-in-chief, appoints cabinet, introduces legislation</p>
                <p className="text-sm text-slate-700"><strong>Selection:</strong> Plurality vote (First-past-the-post). No runoff.</p>
                <p className="text-sm text-slate-700"><strong>Term Limit:</strong> Sexenio (Strict single 6-year term). No reelection allowed.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇳🇬 Nigeria (Federal Republic)</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <p className="text-sm text-slate-700"><strong>Executive:</strong> President (Head of State + Head of Government)</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>Powers:</strong> Commander-in-chief, manages oil revenue, appoints cabinet (must include member from each state)</p>
                <p className="text-sm text-slate-700"><strong>Selection:</strong> Two-round system. Must win majority of votes AND 25% of votes in 2/3rds of states</p>
                <p className="text-sm text-slate-700"><strong>Term Limit:</strong> Two 4-year terms</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇷🇺 Russia (Semi-Presidential / Authoritarian)</h3>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200 mb-4">
              <div className="space-y-3">
                <div>
                  <h4 className="font-bold text-orange-800">President (Putin)</h4>
                  <p className="text-sm text-slate-700 mt-1">Powers: Issues decrees with force of law, Commander-in-chief, appoints PM, dissolves Duma</p>
                  <p className="text-xs text-orange-600 mt-1">Selection: Two-round majority runoff</p>
                </div>
                <div>
                  <h4 className="font-bold text-orange-800">Prime Minister</h4>
                  <p className="text-sm text-slate-700 mt-1">Powers: Oversees domestic economy and civil service. Can become Acting President if President resigns/dies.</p>
                  <p className="text-xs text-orange-600 mt-1">Selection: Nominated by President, confirmed by Duma</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🇬🇧 United Kingdom (Parliamentary Democracy)</h3>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 mb-4">
              <div className="space-y-3">
                <div>
                  <h4 className="font-bold text-indigo-800">Head of State: Monarch</h4>
                  <p className="text-sm text-slate-700 mt-1">Ceremonial; gives "Royal Assent" to laws</p>
                </div>
                <div>
                  <h4 className="font-bold text-indigo-800">Head of Government: Prime Minister</h4>
                  <p className="text-sm text-slate-700 mt-1">Sets foreign/domestic policy, "First Among Equals" in Cabinet</p>
                  <p className="text-xs text-indigo-600 mt-1">Selection: Leader of majority party in House of Commons</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-4",
      title: "Topic 2.4: Executive Term Limits",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✅ Advantages of Term Limits</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm text-slate-700">✓ Check on power; prevents dictatorship (personality rule)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm text-slate-700">✓ Promotes new ideas and leadership circulation</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-sm text-slate-700">✓ Focuses officeholder on governance rather than perpetual campaigning</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">❌ Disadvantages of Term Limits</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-sm text-slate-700">✗ Forces experienced leaders out (brain drain)</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-sm text-slate-700">✗ Creates "Lame Duck" periods where executive has little influence</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-sm text-slate-700">✗ Insufficient time to achieve long-term policy goals</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Country Specifics</h3>
            <div className="space-y-2">
              <p className="text-sm text-slate-700"><strong>🇨🇳 China:</strong> Limits removed in 2018. President/General Secretary can serve indefinitely.</p>
              <p className="text-sm text-slate-700"><strong>🇲🇽 Mexico:</strong> Sexenio (Strict single 6-year term). No reelection allowed.</p>
              <p className="text-sm text-slate-700"><strong>🇳🇬 Nigeria:</strong> Two 4-year terms.</p>
              <p className="text-sm text-slate-700"><strong>🇷🇺 Russia:</strong> Two consecutive 6-year terms. (Putin circumvented by becoming PM in 2008, then resetting constitution in 2020)</p>
              <p className="text-sm text-slate-700"><strong>🇮🇷 Iran:</strong> President limited to two consecutive 4-year terms (can run again after sitting out).</p>
              <p className="text-sm text-slate-700"><strong>🇬🇧 UK:</strong> No fixed term limits. PM serves as long as they maintain confidence of party and Parliament.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-5",
      title: "Topic 2.5: Removal of Executives",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗳️ Vote of No Confidence (Parliamentary)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Mechanism</h4>
              <p className="text-sm text-slate-700 mt-2">Legislature votes on whether they support the government. If vote fails, entire cabinet/PM must resign or call new elections.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Context:</strong> Used in UK. Theoretically used in Russia (Duma can vote no confidence, but President can ignore it once; if voted twice, President dissolves Duma)</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Impeachment (Presidential)</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <h4 className="font-bold text-purple-800">Mechanism</h4>
              <p className="text-sm text-slate-700 mt-2">Legal process to charge and remove an executive for crimes.</p>
              <div className="mt-3 space-y-2">
                <p className="text-sm text-slate-700"><strong>🇳🇬 Nigeria:</strong> National Assembly can impeach</p>
                <p className="text-sm text-slate-700"><strong>🇷🇺 Russia:</strong> Extremely difficult. Requires charges of treason/grave crimes confirmed by Supreme Court and Constitutional Court, then 2/3 vote in both houses</p>
                <p className="text-sm text-slate-700"><strong>🇮🇷 Iran:</strong> Majles can impeach President (Supreme Leader makes final decision)</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗳️ Elections</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700">The primary method of removal in democratic systems (UK, Mexico, Nigeria).</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-6",
      title: "Topic 2.6: Legislative Systems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Unicameral vs. Bicameral</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Unicameral</h4>
                <p className="text-sm text-slate-700 mt-2">One house. Common in unitary states or authoritarian regimes to centralize efficiency.</p>
                <p className="text-xs text-blue-600 mt-2">Examples: China, Iran</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Bicameral</h4>
                <p className="text-sm text-slate-700 mt-2">Two houses. Common in federal states to represent population (lower) and regions (upper).</p>
                <p className="text-xs text-purple-600 mt-2">Examples: Mexico, Nigeria, Russia, UK</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Country Specifics</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China (Unicameral)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>National People's Congress (NPC):</strong> Constitutionally highest organ of state power, but practically a "rubber stamp" for party decisions. Meets 2 weeks/year.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇮🇷 Iran (Unicameral)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Majles:</strong> Elected body. Drafts legislation, ratifies treaties, approves budget. All laws must be approved by Guardian Council for compatibility with Islam/Constitution.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 United Kingdom (Bicameral)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>House of Commons (Lower):</strong> Elected, supreme legislative power.</p>
                <p className="text-sm text-slate-700"><strong>House of Lords (Upper):</strong> Unelected (appointed/hereditary). Can delay bills but not block them.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia (Bicameral)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>State Duma (Lower):</strong> Elected. Passes legislation, confirms PM.</p>
                <p className="text-sm text-slate-700"><strong>Federation Council (Upper):</strong> Represents regions (2 members per region). Approves troop deployments/treaties.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇲🇽 Mexico (Bicameral)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Chamber of Deputies (Lower):</strong> Budget/Fiscal bills.</p>
                <p className="text-sm text-slate-700"><strong>Senate (Upper):</strong> Foreign policy/Treaties.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-800">🇳🇬 Nigeria (Bicameral)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>House of Representatives (Lower):</strong> Population-based.</p>
                <p className="text-sm text-slate-700"><strong>Senate (Upper):</strong> 3 per state (equal representation).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-7",
      title: "Topic 2.7: Independent Legislatures",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔗 Constraints on Legislative Independence</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2">NPC is constrained by the CCP (Politburo Standing Committee). Party dictates policy; NPC ratifies it.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇮🇷 Iran</h4>
                <p className="text-sm text-slate-700 mt-2">Majles is constrained by Guardian Council (vets candidates and vetoes laws) and Expediency Council (mediates disputes, appointed by SL).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia</h4>
                <p className="text-sm text-slate-700 mt-2">Duma is dominated by United Russia (party of power loyal to Putin), turning it into a rubber stamp despite constitutional powers.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✨ Enhancing Legislative Independence</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 United Kingdom</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Question Time:</strong> MPs can grill the PM weekly, providing oversight and accountability.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2">National Assembly rejected President Obasanjo's attempt to change constitution for 3rd term (shows moments of independence).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇲🇽 Mexico</h4>
                <p className="text-sm text-slate-700 mt-2">Since end of PRI dominance (2000), legislature is no longer a rubber stamp; divided government forces negotiation.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-8",
      title: "Topic 2.8: Judicial Systems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Rule of Law vs. Rule by Law</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Rule of Law (Democratic)</h4>
                <p className="text-sm text-slate-700 mt-2">State is subject to the law. Law applies equally to everyone (citizens and leaders).</p>
                <p className="text-xs text-green-600 mt-2">Examples: UK, Mexico (improving), Nigeria (struggling)</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Rule BY Law (Authoritarian)</h4>
                <p className="text-sm text-slate-700 mt-2">State uses law as a tool to control citizens. Leadership is often above the law.</p>
                <p className="text-xs text-red-600 mt-2">Examples: China, Russia</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Judicial Systems in Course Countries</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2">Judiciary serves the Party. 99% conviction rate in criminal cases. Used to silence dissent.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇮🇷 Iran</h4>
                <p className="text-sm text-slate-700 mt-2">Based on Sharia Law (Islamic Law). Supreme Leader appoints Chief Justice. Courts enforce religious conformity.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia</h4>
                <p className="text-sm text-slate-700 mt-2">Constitutional Court exists but rarely challenges President. Legal system used to target political opposition (e.g., Navalny).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2">Combines English Common Law (federal/south) and Sharia Law (12 northern states). Supreme Court has judicial review.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇲🇽 Mexico</h4>
                <p className="text-sm text-slate-700 mt-2">Transitioning from PRI-controlled courts to independent system. Supreme Court has judicial review.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h4 className="font-bold text-indigo-800">🇬🇧 United Kingdom</h4>
                <p className="text-sm text-slate-700 mt-2">Common Law system (based on precedent). Supreme Court (est. 2009) is highest court but cannot overturn Acts of Parliament (Parliamentary Sovereignty).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-2-9",
      title: "Topic 2.9: Independent Judiciaries",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">✅ Requirements for Independence</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Tenure" 
                definition="Judges serve for life or long fixed terms (protects them from firing for unpopular decisions)."
              />
              <KeyTerm 
                term="Appointment" 
                definition="Professional qualifications rather than political loyalty."
              />
              <KeyTerm 
                term="Autonomy" 
                definition="Ability to overrule the executive/legislature (Judicial Review)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💪 Importance of Independence</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Democracy</h4>
                <p className="text-sm text-slate-700 mt-2">Protects civil liberties and civil rights.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Economic</h4>
                <p className="text-sm text-slate-700 mt-2">Enforces contracts, encouraging foreign investment.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">Corruption</h4>
                <p className="text-sm text-slate-700 mt-2">Checks the abuse of power by the executive.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Country Comparison of Independence</h3>
            <div className="space-y-2">
              <p className="text-sm text-slate-700"><strong>🟢 High Independence:</strong> 🇬🇧 UK (Supreme Court)</p>
              <p className="text-sm text-slate-700"><strong>🟡 Increasing Independence:</strong> 🇲🇽 Mexico (Judicial reforms since 1990s), 🇳🇬 Nigeria (Asserting authority, though corruption remains)</p>
              <p className="text-sm text-slate-700"><strong>🔴 Low Independence:</strong> 🇨🇳 China (Controlled by CCP), 🇷🇺 Russia (Controlled by Putin/Kremlin), 🇮🇷 Iran (Controlled by Supreme Leader/Religious doctrine)</p>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default governmentUnit2Notes;